import React, { useEffect, useState } from "react";
import {
    MapContainer, TileLayer, Marker, Popup,
    Polyline, CircleMarker, ZoomControl, useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import L from "leaflet";
import { baseurladdress } from "../baseurl";

/* ── Icons ── */
const blueIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41],
});
const greenIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41],
});

const purpleIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

/* ── Auto-fit map to path ── */
const FitBounds = ({ coords }) => {
    const map = useMap();
    useEffect(() => {
        if (coords.length > 0) map.fitBounds(coords, { padding: [50, 50], maxZoom: 18 });
    }, [coords, map]);
    return null;
};

/* ── Client marker icons ── */
const redIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41],
});
const goldIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41],
});

const CLIENT_TYPE_LABEL = { 0: "Doctor", 1: "Hospital", 2: "Clinic" };

/* ── Inline Session Map ── */
const SessionMap = ({ detail, visitData, geofenceRadius }) => {
    const locations = detail?.locations?.filter((p) => p.lat && p.lng) || [];
    const coords = locations.map((p) => [Number(p.lat), Number(p.lng)]);
    const radius = geofenceRadius || 50; // metres

    // Collect all coords (staff path + client locations) for bounds
    const clientCoords = (visitData || [])
        .filter(v => v.client_lat && v.client_lng)
        .map(v => [v.client_lat, v.client_lng]);
    const allCoords = [...coords, ...clientCoords];

    if (coords.length === 0)
        return <div style={{ textAlign: "center", padding: 40, color: "#aaa", fontSize: 13 }}>No GPS coordinates available.</div>;

    return (
        <MapContainer center={coords[0]} zoom={13}
            className="session-map" style={{ width: "100%", borderRadius: 8, zIndex: 0 }} zoomControl={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds coords={allCoords.length > 0 ? allCoords : coords} />

            {/* Staff travel path */}
            {coords.length > 1 && <Polyline positions={coords} color="#1976d2" weight={4} opacity={0.85} />}

            {/* GPS checkpoints */}
            {locations.map((p, i) => (
                <CircleMarker key={i} center={[p.lat, p.lng]} radius={4}
                    fillColor="#ff9800" fillOpacity={0.9} color="white" weight={2}>
                    <Popup>
                        <div style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.7 }}>
                            <span style={{ color: "#888" }}>Lat:</span> {Number(p.lat).toFixed(6)}<br />
                            <span style={{ color: "#888" }}>Lon:</span> {Number(p.lng).toFixed(6)}
                        </div>
                    </Popup>
                </CircleMarker>
            ))}

            {/* Start marker */}
            <Marker position={coords[0]} icon={purpleIcon}>
                <Popup>
                    <div style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.7 }}>
                        <b>🟢 Session Start</b><br />
                        <span style={{ color: "#888" }}>Lat:</span> {coords[0][0].toFixed(6)}<br />
                        <span style={{ color: "#888" }}>Lon:</span> {coords[0][1].toFixed(6)}
                    </div>
                </Popup>
            </Marker>

            {/* End marker */}
            {coords.length > 1 && (
                <Marker position={coords[coords.length - 1]} icon={blueIcon}>
                    <Popup>
                        <div style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.7 }}>
                            <b>🔵 Session End</b><br />
                            <span style={{ color: "#888" }}>Lat:</span> {coords[coords.length - 1][0].toFixed(6)}<br />
                            <span style={{ color: "#888" }}>Lon:</span> {coords[coords.length - 1][1].toFixed(6)}
                        </div>
                    </Popup>
                </Marker>
            )}

            {/* Client geofence circles + markers */}
            {(visitData || []).map((v, i) => {
                if (!v.client_lat || !v.client_lng) return null;
                const pos = [v.client_lat, v.client_lng];
                const visited = v.visited === 1;
                const typeLabel = CLIENT_TYPE_LABEL[v.appoint_clienttype] || "Client";
                return (
                    <React.Fragment key={`client-${i}`}>
                        {/* Geofence radius circle */}
                        <CircleMarker
                            center={pos}
                            radius={0}
                            pathOptions={{ color: "transparent" }}
                        />
                        {/* Use L.circle for real-world metre radius */}
                        <ClientGeofenceCircle
                            center={pos}
                            radius={radius}
                            visited={visited}
                        />
                        {/* Client pin */}
                        <Marker position={pos} icon={visited ? greenIcon : redIcon}>
                            <Popup>
                                <div style={{ fontFamily: "'Segoe UI', sans-serif", fontSize: 13, lineHeight: 1.8, minWidth: 190 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "#1a237e" }}>
                                        {v.appoint_clientname}
                                    </div>
                                    <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 6 }}>{typeLabel}</div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                                        <span style={{
                                            display: "inline-block", padding: "4px 12px",
                                            borderRadius: 20, fontWeight: 700, fontSize: 12,
                                            background: visited ? "#16a34a" : "#dc2626",
                                            color: "white",
                                            letterSpacing: 0.4,
                                            boxShadow: visited ? "0 2px 6px rgba(22,163,74,0.35)" : "0 2px 6px rgba(220,38,38,0.35)",
                                        }}>
                                            {visited ? "✅ Visited" : "❌ Not Visited"}
                                        </span>
                                    </div>
                                    {!visited && v.closest_distance_m !== null && (
                                        <div style={{ fontSize: 12, color: "#e65100" }}>
                                            Closest approach: <b>{v.closest_distance_m} m</b>
                                        </div>
                                    )}
                                    {v.dwell_minutes > 0 && (
                                        <div style={{ fontSize: 12, color: "#374151" }}>
                                            Time in zone: <b>{v.dwell_minutes} min</b>
                                        </div>
                                    )}
                                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                                        Geofence radius: {radius} m
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    </React.Fragment>
                );
            })}

            <ZoomControl position="topright" />
        </MapContainer>
    );
};

/* Helper: renders a real-world metre-radius circle using Leaflet's L.circle via useEffect */
const ClientGeofenceCircle = ({ center, radius, visited }) => {
    const map = useMap();
    useEffect(() => {
        const circle = L.circle(center, {
            radius,
            color: visited ? "#16a34a" : "#dc2626",
            fillColor: visited ? "#22c55e" : "#ef4444",
            fillOpacity: 0.12,
            weight: 2,
            dashArray: visited ? null : "6 4",
        }).addTo(map);
        return () => circle.remove();
    }, [center, radius, visited, map]);
    return null;
};


/* ── Appointment Detail Modal ── */
const AppointmentModal = ({ appointment, onClose }) => {
    if (!appointment) return null;
    const fmtTime = (ds) => {
        if (!ds) return "N/A";
        if (/^\d{2}:\d{2}(:\d{2})?$/.test(ds.trim())) {
            const [h, m] = ds.split(":");
            const d = new Date();
            d.setHours(Number(h), Number(m), 0);
            return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
        }
        const d = new Date(ds);
        return isNaN(d) ? ds : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    };

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
        }} onClick={onClose}>
            <div style={{
                background: "white", borderRadius: 14, width: "100%", maxWidth: 480,
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                overflow: "hidden", animation: "fadeIn 0.18s ease",
            }} onClick={(e) => e.stopPropagation()}>

                {/* Modal Header — original purple gradient */}
                <div style={{
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    padding: "18px 22px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                    <div>
                        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
                            Appointment Details
                        </div>
                        <div style={{ color: "white", fontWeight: 700, fontSize: 17, marginTop: 3 }}>
                            {appointment.appoint_clientname}
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: "rgba(255,255,255,0.2)", border: "none", color: "white",
                        borderRadius: "50%", width: 32, height: 32, cursor: "pointer",
                        fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                    }}>✕</button>
                </div>

                <div style={{ padding: "22px 24px" }}>
                    {[
                        { icon: "🆔", label: "Appointment ID", value: `#${appointment.appoint_id}` },
                        { icon: "👤", label: "Client Name", value: appointment.appoint_clientname },
                        {
                            icon: "📅", label: "Appointment Date", value: appointment.appoint_appointmentdate
                                ? (() => {
                                    const d = new Date(appointment.appoint_appointmentdate);
                                    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
                                })() : "N/A"
                        },
                        { icon: "🕐", label: "Time From", value: fmtTime(appointment.appoint_timefrom) },
                        { icon: "🕑", label: "Time To", value: fmtTime(appointment.appoint_timeto) },
                        {
                            icon: "⏱", label: "Duration", value: (() => {
                                if (!appointment.appoint_timefrom || !appointment.appoint_timeto) return "N/A";
                                const toDate = (ds) => {
                                    if (/^\d{2}:\d{2}(:\d{2})?$/.test(String(ds).trim())) {
                                        const [h, m, s] = ds.split(":");
                                        const d = new Date(); d.setHours(Number(h), Number(m), Number(s || 0), 0); return d;
                                    }
                                    return new Date(ds);
                                };
                                const diff = (toDate(appointment.appoint_timeto) - toDate(appointment.appoint_timefrom)) / 60000;
                                return diff > 0 ? `${Math.floor(diff)} min` : "N/A";
                            })()
                        },
                    ].map(({ icon, label, value }) => (
                        <div key={label} style={{
                            display: "flex", alignItems: "flex-start", gap: 14,
                            padding: "11px 0", borderBottom: "1px solid #f0f0f0",
                        }}>
                            <span style={{ fontSize: 18, width: 24, textAlign: "center", flexShrink: 0 }}>{icon}</span>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
                                <div style={{ fontSize: 14, color: "#333", fontWeight: 600 }}>{value}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-12px); } to { opacity:1; transform:translateY(0); } }`}</style>
        </div>
    );
};


/* ════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════ */
const SessionTrackReport = () => {
    const token = localStorage.getItem("token");

    /* ── Today's date string (YYYY-MM-DD) ── */
    const todayStr = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    })();

    /* ── State ── */
    const [allStaff, setAllStaff] = useState([]);
    const [selectedStaffId, setSelectedStaffId] = useState("");
    const [selectedStaffName, setSelectedStaffName] = useState("");
    const [allSessions, setAllSessions] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [sessionDetails, setSessionDetails] = useState({});
    const [expandedSession, setExpandedSession] = useState(null);
    const [loadingPath, setLoadingPath] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);

    /* ── Today's active staff ── */
    const [todayStaff, setTodayStaff] = useState([]);
    const [todayStaffStats, setTodayStaffStats] = useState({});     // { [staff_id]: { total, completed, active, totalSessions, sessions, appointments, sessionDetails } }
    const [todayStatsLoading, setTodayStatsLoading] = useState(true);
    const [todayExpandedSession, setTodayExpandedSession] = useState(null); // session id expanded in today table

    /* ── Appointment state ── */
    const [allAppointments, setAllAppointments] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [selectedAppointment, setSelectedAppointment] = useState(null);

    /* ── Geofencing state ── */
    const [visitChecks, setVisitChecks] = useState({});  // { [sessionId]: visitData[] }
    const [geofenceRadius, setGeofenceRadius] = useState(50); // metres

    /* ── Total visit count (appoint_status = 1) for selected staff ── */
    const [totalVisitCount, setTotalVisitCount] = useState(null); // null = not loaded yet

    /* ── Fetch staff list + today's active staff + their stats ── */
    useEffect(() => {
        axios.get(baseurladdress + "livetrack/getstaffwithsessions", {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            if (res.data.status === "Success") setAllStaff(res.data.data);
        });

        axios.get(baseurladdress + "livetrack/getstafftoday", {
            headers: { Authorization: `Bearer ${token}` },
        }).then(async (res) => {
            if (res.data.status === "Success") {
                const staff = res.data.data;
                setTodayStaff(staff);
                const t2 = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
                const toLocalDate = (ds) => { if (!ds) return ""; const d = new Date(ds); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

                const statsMap = {};
                await Promise.all(staff.map(async (s) => {
                    try {
                        const [sessRes, apptRes, visitRes] = await Promise.all([
                            axios.get(baseurladdress + `livetrack/getstaffsessions/${s.staff_id}`, { headers: { Authorization: `Bearer ${token}` } }),
                            axios.get(baseurladdress + `appointment/getbystaff/${s.staff_id}`, { headers: { Authorization: `Bearer ${token}` } }),
                            axios.get(baseurladdress + `livetrack/totalvisits/${s.staff_id}`, { headers: { Authorization: `Bearer ${token}` } }),
                        ]);
                        const todaySessions = sessRes.data.status === "Success"
                            ? sessRes.data.data.filter((x) => toLocalDate(x.start_time) === t2)
                            : [];
                        const todayAppts = apptRes.data.status === "Success"
                            ? apptRes.data.data.filter((a) => a.appoint_appointmentdate && toLocalDate(a.appoint_appointmentdate) === t2)
                            : [];
                        const allTimeVisits = visitRes.data?.status === "Success"
                            ? visitRes.data.total_visits
                            : 0;

                        // Pre-fetch path details for each session
                        const sessionDetails = {};                                 await Promise.all(todaySessions.map(async (sess) => { 
                            try {
                                const r = await axios.get(baseurladdress + `livetrack/getsessionpath/${sess.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                if (r.data.status === "Success") sessionDetails[sess.id] = r.data.data;
                            } catch (_) { }
                        }));

                        statsMap[s.staff_id] = {
                            total: allTimeVisits,
                            completed: todaySessions.filter((x) => x.end_time).length,
                            active: todaySessions.filter((x) => !x.end_time).length,
                            totalSessions: todaySessions.length,
                            sessions: todaySessions,
                            appointments: todayAppts,
                            sessionDetails,
                        };
                    } catch (e) {
                        statsMap[s.staff_id] = { total: 0, completed: 0, active: 0, totalSessions: 0, sessions: [], appointments: [], sessionDetails: {} };
                    }
                }));
                setTodayStaffStats(statsMap);
                setTodayStatsLoading(false);
            }
        });
    }, [token]);

    /* ── Helper: local YYYY-MM-DD ── */
    const localDateStr = (ds) => {
        if (!ds) return "";
        const d = new Date(ds);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };

    /* ── Date-range filter helpers ── */
    const applyDateRange = (arr, from, to) => {
        if (!from && !to) return arr;
        return arr.filter((s) => {
            const d = localDateStr(s.start_time);
            if (from && d < from) return false;
            if (to && d > to) return false;
            return true;
        });
    };
    const applyApptRange = (arr, from, to) => {
        if (!from && !to) return arr;
        return arr.filter((a) => {
            const d = localDateStr(a.appoint_appointmentdate);
            if (from && d < from) return false;
            if (to && d > to) return false;
            return true;
        });
    };

    /* ── Fetch sessions when staff selected ── */
    const fetchSessions = async (staffId, fromD, toD) => {
        setSessions([]); setAllSessions([]); setSessionDetails({});
        setExpandedSession(null); setAllAppointments([]); setAppointments([]);
        setTotalVisitCount(null);
        setLoading(true);
        try {
            const sessRes = await axios.get(
                baseurladdress + `livetrack/getstaffsessions/${staffId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (sessRes.data.status === "Success") {
                const withPoints = sessRes.data.data.filter((s) => s.location_points > 0);
                setAllSessions(withPoints);
                setSessions(applyDateRange(withPoints, fromD, toD));
                fetchAllSessionDetails(withPoints);
            }
        } catch (err) {
            console.error("Sessions fetch error:", err);
        } finally {
            setLoading(false);
        }
        try {
            const apptRes = await axios.get(
                baseurladdress + `appointment/getbystaff/${staffId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (apptRes.data.status === "Success") {
                setAllAppointments(apptRes.data.data);
                setAppointments(applyApptRange(apptRes.data.data, fromD, toD));
            }
        } catch (err) {
            console.error("Appointments fetch error:", err);
        }

        // Fetch total visit count (appoint_status = 1) from appointments_tbl
        try {
            const visitRes = await axios.get(
                baseurladdress + `livetrack/totalvisits/${staffId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (visitRes.data.status === "Success") {
                setTotalVisitCount(visitRes.data.total_visits);
            }
        } catch (err) {
            console.error("Total visits fetch error:", err);
        }
    };

    /* ── Pre-fetch path details for all sessions ── */
    const fetchAllSessionDetails = async (sessionsArr) => {
        setLoadingDetails(true);
        const details = {};
        await Promise.all(sessionsArr.map(async (s) => {
            try {
                const res = await axios.get(baseurladdress + `livetrack/getsessionpath/${s.id}`,
                    { headers: { Authorization: `Bearer ${token}` } });
                if (res.data.status === "Success") details[s.id] = res.data.data;
            } catch (err) { console.error(`Session ${s.id}:`, err); }
        }));
        setSessionDetails(details);
        setLoadingDetails(false);
    };

    /* ── Toggle map expansion ── */
    const handleTogglePath = async (sessionId) => {
        if (expandedSession === sessionId) { setExpandedSession(null); return; }
        setLoadingPath(sessionId);
        try {
            // Fetch path if not already loaded
            let pathData = sessionDetails[sessionId];
            if (!pathData?.locations) {
                const res = await axios.get(baseurladdress + `livetrack/getsessionpath/${sessionId}`,
                    { headers: { Authorization: `Bearer ${token}` } });
                if (res.data.status === "Success") {
                    pathData = res.data.data;
                    setSessionDetails((prev) => ({ ...prev, [sessionId]: pathData }));
                }
            }
            // Always (re-)fetch visit check with current radius
            try {
                const vcRes = await axios.get(
                    baseurladdress + `livetrack/session-visit-check/${sessionId}?radius=${geofenceRadius}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (vcRes.data.status === "Success") {
                    setVisitChecks((prev) => ({ ...prev, [sessionId]: vcRes.data.data }));
                }
            } catch (vcErr) {
                console.error("Visit check fetch error:", vcErr);
            }
            setExpandedSession(sessionId);
        } catch (err) { console.error(err); }
        finally { setLoadingPath(null); }
    };

    /* ── Staff dropdown change — uses current date filters as-is ── */
    const handleStaffChange = (e) => {
        const id = e.target.value;
        const name =
            allStaff.find((s) => String(s.staff_id) === String(id))?.staff_name ||
            todayStaff.find((s) => String(s.staff_id) === String(id))?.staff_name ||
            "";
        setSelectedStaffId(id);
        setSelectedStaffName(name);
        if (id) fetchSessions(id, fromDate, toDate);
        else {
            setSessions([]); setAllSessions([]); setSessionDetails({});
            setExpandedSession(null); setAllAppointments([]); setAppointments([]);
        }
    };

    /* ── Card click — always locks to today's date ── */
    const handleCardClick = (staffId) => {
        const name =
            todayStaff.find((s) => String(s.staff_id) === String(staffId))?.staff_name ||
            allStaff.find((s) => String(s.staff_id) === String(staffId))?.staff_name ||
            "";
        setSelectedStaffId(String(staffId));
        setSelectedStaffName(name);
        setFromDate(todayStr);
        setToDate(todayStr);
        fetchSessions(String(staffId), todayStr, todayStr);
    };

    /* ── Date handlers ── */
    const handleFromDateChange = (val) => {
        setFromDate(val); setExpandedSession(null);
        setSessions(applyDateRange(allSessions, val, toDate));
        setAppointments(applyApptRange(allAppointments, val, toDate));
    };
    const handleToDateChange = (val) => {
        setToDate(val); setExpandedSession(null);
        setSessions(applyDateRange(allSessions, fromDate, val));
        setAppointments(applyApptRange(allAppointments, fromDate, val));
    };
    const handleClearDates = () => {
        setFromDate(""); setToDate(""); setExpandedSession(null);
        setSessions(allSessions); setAppointments(allAppointments);
    };

    /* ── Match appointments to a session by date ── */
    const getSessionAppointments = (session) => {
        const detail = sessionDetails[session.id];
        const rawSessionTime = detail?.start_time || session.start_time;
        if (!rawSessionTime) return [];
        const sessionDate = localDateStr(rawSessionTime);
        return appointments.filter((a) => {
            if (!a.appoint_appointmentdate) return false;
            return localDateStr(a.appoint_appointmentdate) === sessionDate;
        });
    };

    /* ── Helpers ── */
    const fmt = (ds) => {
        if (!ds) return "N/A";
        return new Date(ds).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    };
    const fmtDist = (d) => {
        if (!d) return "N/A";
        return `${d.km} km`;
    };
    const totalDist = () => {
        let t = 0;
        sessions.forEach((s) => {
            const d = sessionDetails[s.id]?.distance;
            if (d) t += parseFloat(d.km) || 0;
        });
        return `${t.toFixed(2)} km`;
    };

    /* ── Print today's active staff table ── */
    const handlePrintToday = () => {
        const fmtLocal = (ds) => ds
            ? new Date(ds).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
            : "—";
        const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

        const rows = todayStaff.map((s) => {
            const staffData = todayStaffStats[s.staff_id];
            const sessions_ = staffData?.sessions || [];
            if (sessions_.length === 0) {
                return `<tr style="background:#fff">
                    <td style="font-weight:600;color:#1a237e">${s.staff_name}</td>
                    <td colspan="6" style="color:#aaa;font-style:italic">No sessions today</td>
                </tr>`;
            }
            return sessions_.map((sess, si) => {
                const detail = staffData.sessionDetails?.[sess.id];
                const startTime = detail?.start_time || sess.start_time;
                const endTime = detail?.end_time || sess.end_time;
                const duration = detail?.duration || "—";
                const dist = detail?.distance ? detail.distance.km + " km" : (sess.distance_km ? sess.distance_km + " km" : "—");
                const sessDate = startTime ? (() => { const d = new Date(startTime); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })() : "";
                const appts = (staffData.appointments || []).filter((a) => {
                    if (!a.appoint_appointmentdate) return false;
                    const d = new Date(a.appoint_appointmentdate);
                    const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    return ds === sessDate;
                });
                const clients = appts.length > 0 ? appts.map((a) => a.appoint_clientname).join(", ") : "—";
                const bg = si % 2 === 0 ? "#fff" : "#f4f7fc";
                return `<tr style="background:${bg}">
                    <td style="font-weight:600;color:#1a237e">${si === 0 ? s.staff_name : ""}</td>
                    <td>${fmtLocal(startTime)}</td>
                    <td>${endTime ? fmtLocal(endTime) : '<span style="color:#16a34a;font-weight:600">● Live</span>'}</td>
                    <td style="font-weight:600">${duration}</td>
                    <td style="font-weight:700;color:#1565c0">${dist}</td>
                    <td style="color:#4527a0;font-weight:600">${clients}</td>
                </tr>`;
            }).join("");
        }).join("");

        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Today's Active Staff</title>
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  html, body { font-family:'Segoe UI',Arial,sans-serif; color:#222; background:#fff;
    -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  body { padding:18px 22px; }
  .hdr { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:14px; flex-wrap:wrap; gap:10px; }
  .hdr-left h2 { color:#1976d2; font-size:20px; font-weight:700; margin-bottom:3px; }
  .hdr-left .meta { color:#666; font-size:11px; }
  .badge { background:linear-gradient(135deg,#22c55e,#16a34a); color:#fff; padding:6px 16px;
    border-radius:20px; font-size:13px; font-weight:700; }
  table { width:100%; border-collapse:collapse; font-size:12px; min-width:640px; }
  thead tr { background:#1976d2; color:#fff; }
  th { padding:10px 12px; text-align:left; font-weight:600; font-size:11.5px; white-space:nowrap;
       border-right:1px solid rgba(255,255,255,0.15); }
  th:last-child { border-right:none; }
  td { padding:10px 12px; border-bottom:1px solid #eef0f4; vertical-align:middle; word-wrap:break-word; }
  .footer { text-align:center; color:#bbb; font-size:10px; margin-top:16px;
    padding-top:10px; border-top:1px solid #eee; }
  @page { size:A4 landscape; margin:8mm 10mm; }
  @media print { body { padding:0; } thead { display:table-header-group; } tr { page-break-inside:avoid; } }
</style>
</head>
<body>
<div class="hdr">
  <div class="hdr-left">
    <h2>Today's Active Staff</h2>
    <div class="meta">Generated: ${new Date().toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" })}</div>
    <div class="meta" style="margin-top:3px">${today}</div>
  </div>
 
</div>
<table>
  <thead>
    <tr>
      <th>Staff Name</th><th>Start Time</th><th>End Time</th>
      <th>Duration</th><th>Distance</th><th>Client</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>
<div class="footer">Staff Path Tracking System &nbsp;|&nbsp; Today's Active Staff Report</div>
</body>
</html>`;

        const old = document.getElementById("__print_frame_today__");
        if (old) old.remove();
        const iframe = document.createElement("iframe");
        iframe.id = "__print_frame_today__";
        const frameW = Math.min(window.innerWidth, 1122);
        iframe.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${frameW}px;height:794px;border:none;visibility:hidden;`;
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow.document;
        doc.open(); doc.write(html); doc.close();
        setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => iframe.remove(), 1500);
        }, 400);
    };

    /* ── Date range label for report header & print ── */
    const dateRangeLabel = () => {
        const fmtD = (ds) => ds
            ? new Date(ds + "T00:00:00").toLocaleDateString("en-IN", { dateStyle: "long" })
            : null;
        const f = fmtD(fromDate), t = fmtD(toDate);
        if (f && t && fromDate === toDate) return f;
        if (f && t) return `${f} – ${t}`;
        if (f) return `From ${f}`;
        if (t) return `Until ${t}`;
        return "All Dates";
    };

    /* ── Print ── */
    const handlePrint = () => {
        const fmtLocal = (ds) => ds
            ? new Date(ds).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
            : "—";

        const rows = sessions.map((session, idx) => {
            const detail = sessionDetails[session.id];
            const startTime = detail?.start_time || session.start_time;
            const endTime = detail?.end_time;
            const duration = detail?.duration || "—";
            const dist = detail?.distance ? detail.distance.km + " km" : "—";
            const gpsPts = detail?.distance?.points ?? session.location_points ?? "—";
            const appts = getSessionAppointments(session);
            const clientNames = appts.length > 0 ? appts.map((a) => a.appoint_clientname).join(", ") : "—";
            const bg = idx % 2 === 0 ? "#fff" : "#f4f7fc";
            return `<tr style="background:${bg}">`
                + `<td style="text-align:center;color:#888">${idx + 1}</td>`
                + `<td>${fmtLocal(startTime)}</td>`
                + `<td>${fmtLocal(endTime)}</td>`
                + `<td style="font-weight:600">${duration}</td>`
                + `<td style="text-align:left;font-weight:700;color:#1565c0">${dist}</td>`
                + `<td style="text-align:left">${gpsPts}</td>`
                + `<td style="color:#4527a0;font-weight:600">${clientNames}</td>`
                + `</tr>`;
        }).join("");

        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Staff Session Report</title>
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  html, body { width:100%; font-family:'Segoe UI',Arial,sans-serif; color:#222; background:#fff;
    -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  body { padding:18px 22px; }
  .hdr { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:14px; flex-wrap:wrap; gap:10px; }
  .hdr-left h2 { color:#1976d2; font-size:20px; font-weight:700; margin-bottom:3px; }
  .hdr-left .meta { color:#666; font-size:11px; }
  .hdr-right { background:linear-gradient(135deg,#667eea,#764ba2); color:#fff; padding:10px 16px;
    border-radius:8px; text-align:right; min-width:140px; }
  .hdr-right .staff-name { font-size:15px; font-weight:700; }
  .hdr-right .date-label { font-size:10px; opacity:0.85; margin-top:2px; }
  .stats { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
  .sbox { flex:1 1 120px; border:1.5px solid #dde8f8; border-radius:7px; padding:9px 8px;
    text-align:center; background:#f8faff; }
  .sbox .lbl { font-size:8.5px; color:#999; text-transform:uppercase; letter-spacing:.6px; margin-bottom:3px; }
  .sbox .val { font-size:20px; font-weight:700; color:#1976d2; }
  .tbl-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  table { width:100%; border-collapse:collapse; font-size:12px; table-layout:fixed; min-width:620px; }
  colgroup col.c-no   { width:4%; }
  colgroup col.c-st   { width:18%; }
  colgroup col.c-et   { width:18%; }
  colgroup col.c-dur  { width:12%; }
  colgroup col.c-dist { width:12%; }
  colgroup col.c-gps  { width:8%; }
  colgroup col.c-cli  { width:28%; }
  thead tr { background:#1976d2; color:#fff; }
  th { padding:10px 12px; text-align:left; font-weight:600; font-size:11.5px; white-space:nowrap;
       border-right:1px solid rgba(255,255,255,0.15); }
  th:last-child { border-right:none; }
  td { padding:10px 12px; border-bottom:1px solid #eef0f4; vertical-align:middle; word-wrap:break-word; }
  tbody tr:nth-child(even) td { background:#f4f7fc; }
  .footer { text-align:center; color:#bbb; font-size:10px; margin-top:16px;
    padding-top:10px; border-top:1px solid #eee; }
  @media (max-width:600px) {
    body { padding:12px 14px; }
    .hdr { flex-direction:column; }
    .hdr-right { width:100%; text-align:left; }
    .sbox { flex:1 1 calc(50% - 8px); }
  }
  @page { size:A4 landscape; margin:8mm 10mm; }
  @media print {
    body { padding:0; }
    .tbl-wrap { overflow:visible !important; }
    table { min-width:unset !important; }
    thead { display:table-header-group; }
    tr { page-break-inside:avoid; }
  }
</style>
</head>
<body>
<div class="hdr">
  <div class="hdr-left">
    <h2>Staff Report</h2>
    <div class="meta">Generated: ${new Date().toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" })}</div>
  </div>
  <div class="hdr-right">
    <div class="staff-name">${selectedStaffName}</div>
    <div class="date-label">${dateRangeLabel()}</div>
  </div>
</div>
<div class="stats">
  <div class="sbox"><div class="lbl">Completed</div><div class="val">${sessions.filter((s) => !sessionDetails[s.id]?.is_active).length}</div></div>
  <div class="sbox"><div class="lbl">Active</div><div class="val">${sessions.filter((s) => sessionDetails[s.id]?.is_active).length}</div></div>
  <div class="sbox"><div class="lbl">Total Distance</div><div class="val">${totalDist()}</div></div>
  <div class="sbox"><div class="lbl">Total Visits</div><div class="val">${totalVisitCount !== null ? totalVisitCount : appointments.filter((a) => a.appoint_status === 1).length}</div></div>
</div>
<div class="tbl-wrap">
<table>
  <colgroup>
    <col class="c-no"/><col class="c-st"/><col class="c-et"/>
    <col class="c-dur"/><col class="c-dist"/><col class="c-gps"/><col class="c-cli"/>
  </colgroup>
  <thead>
    <tr>
      <th>#</th><th>Start Time</th><th>End Time</th>
      <th>Duration</th><th>Distance</th><th>GPS Pts</th><th>Client Name</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>
</div>
<div class="footer">Staff Path Tracking System &nbsp;|&nbsp; Report for <strong>${selectedStaffName}</strong></div>
</body>
</html>`;

        const old = document.getElementById("__print_frame__");
        if (old) old.remove();
        const iframe = document.createElement("iframe");
        iframe.id = "__print_frame__";
        const frameW = Math.min(window.innerWidth, 1122);
        iframe.style.cssText = [
            "position:fixed", "top:-9999px", "left:-9999px",
            `width:${frameW}px`, "height:794px", "border:none", "visibility:hidden",
        ].join(";");
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow.document;
        doc.open(); doc.write(html); doc.close();
        setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => iframe.remove(), 1500);
        }, 400);
    };


    /* ════════════════════════════════════
       RENDER
    ════════════════════════════════════ */
    return (
        <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", background: "#f0f2f7", minHeight: "100vh" }}>

            {/* ── Appointment Detail Modal ── */}
            <AppointmentModal appointment={selectedAppointment} onClose={() => setSelectedAppointment(null)} />

            {/* ══════════ TOP NAV BAR ══════════ */}
            <div className="no-print controls-bar" style={{
                background: "linear-gradient(135deg, #1a237e 0%, #1976d2 100%)",
                padding: "0 28px",
                display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
                boxShadow: "0 2px 12px rgba(25,118,210,0.3)",
                minHeight: 60,
            }}>
                {/* Page title */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 8 }}>

                    <span style={{ color: "white", fontWeight: 700, fontSize: 15, letterSpacing: 0.3, whiteSpace: "nowrap" }}>
                        Session Tracker
                    </span>
                </div>

                {/* Divider */}
                <div style={{ width: 1, height: 32, background: "rgba(255,255,255,0.2)" }} />

                {/* Staff selector */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.5 }}>Staff</label>
                    <select value={selectedStaffId} onChange={handleStaffChange} style={{
                        padding: "7px 12px", borderRadius: 8,
                        border: "1.5px solid rgba(255,255,255,0.25)",
                        fontSize: 13, color: "#1a237e", background: "white",
                        cursor: "pointer", minWidth: 200, outline: "none", fontWeight: 500,
                    }}>
                        <option value="">— Select staff —</option>
                        {allStaff.map((s) => (
                            <option key={s.staff_id} value={s.staff_id}>{s.staff_name}</option>
                        ))}
                    </select>
                </div>

                {/* Divider */}
                <div style={{ width: 1, height: 32, background: "rgba(255,255,255,0.2)" }} />

                {/* From date */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.5 }}>From</label>
                    <input type="date" value={fromDate} max={toDate || undefined}
                        onChange={(e) => handleFromDateChange(e.target.value)}
                        style={{
                            padding: "7px 10px", borderRadius: 8,
                            border: "1.5px solid rgba(255,255,255,0.25)",
                            fontSize: 13, color: "#1a237e", background: "white",
                            cursor: "pointer", outline: "none", fontWeight: 500,
                        }} />
                </div>

                {/* To date */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.5 }}>To</label>
                    <input type="date" value={toDate} min={fromDate || undefined}
                        onChange={(e) => handleToDateChange(e.target.value)}
                        style={{
                            padding: "7px 10px", borderRadius: 8,
                            border: "1.5px solid rgba(255,255,255,0.25)",
                            fontSize: 13, color: "#1a237e", background: "white",
                            cursor: "pointer", outline: "none", fontWeight: 500,
                        }} />
                </div>

                {/* Clear dates */}
                {(fromDate || toDate) && (
                    <button onClick={handleClearDates} style={{
                        padding: "6px 13px", borderRadius: 7,
                        border: "1.5px solid rgba(255,255,255,0.35)",
                        background: "rgba(255,255,255,0.12)",
                        color: "rgba(255,255,255,0.9)", fontSize: 12,
                        fontWeight: 500, cursor: "pointer",
                    }}>✕ Clear</button>
                )}

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Print button */}
                {sessions.length > 0 && (
                    <button onClick={handlePrint} style={{
                        padding: "7px 18px",
                        background: "rgba(255,255,255,0.18)",
                        color: "white", border: "1.5px solid rgba(255,255,255,0.35)",
                        borderRadius: 8, cursor: "pointer", fontWeight: 600,
                        fontSize: 13, display: "flex", alignItems: "center", gap: 7,
                        backdropFilter: "blur(4px)",
                    }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9" />
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            <rect x="6" y="14" width="12" height="8" />
                        </svg>
                        Print
                    </button>
                )}
            </div>

            {/* ── Body ── */}
            <div className="report-body" style={{ maxWidth: 1200, margin: "28px auto", padding: "0 24px" }}>

                {/* ── Today's Active Staff (default view) ── */}
                {!selectedStaffId && !loading && (
                    <div style={{ background: "white", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", overflow: "hidden" }}>

                        {/* Panel header */}
                        <div style={{
                            background: "linear-gradient(135deg, #f8faff 0%, #eef3ff 100%)",
                            padding: "20px 28px", borderBottom: "1px solid #e3eaf7",
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>

                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 16, color: "#1a237e" }}>Today's Active Staff</div>
                                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                                        {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                                    </div>
                                </div>
                            </div>
                            {todayStaff.length > 0 && (
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>

                                    <button onClick={handlePrintToday} style={{
                                        display: "flex", alignItems: "center", gap: 6,
                                        padding: "6px 14px", borderRadius: 8,
                                        border: "1.5px solid #1976d2", background: "#eff6ff",
                                        color: "#1d4ed8", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="6 9 6 2 18 2 18 9" />
                                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                            <rect x="6" y="14" width="12" height="8" />
                                        </svg>
                                        Print
                                    </button>
                                </div>
                            )}
                        </div>

                        <div style={{ padding: "24px 28px 28px" }}>
                            {todayStaff.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af" }}>
                                    <div style={{ fontSize: 48 }}>📭</div>
                                    <div style={{ marginTop: 12, fontSize: 15, fontWeight: 500 }}>No staff sessions recorded today.</div>
                                    <div style={{ marginTop: 6, fontSize: 13 }}>Use the dropdown above to browse all staff history.</div>
                                </div>
                            ) : (
                                <>
                                    {/* ── Detail div ABOVE the table (shown on name click) ── */}
                                    {/* ── Sessions table: one row per session across all today's staff ── */}
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed", minWidth: 800 }}>
                                            <colgroup>
                                                <col style={{ width: "18%" }} />
                                                <col style={{ width: "14%" }} />
                                                <col style={{ width: "14%" }} />
                                                <col style={{ width: "10%" }} />
                                                <col style={{ width: "10%" }} />
                                                <col style={{ width: "20%" }} />
                                                <col style={{ width: "14%" }} />
                                            </colgroup>
                                            <thead>
                                                <tr style={{ background: "linear-gradient(135deg, #1a237e, #1976d2)", color: "white" }}>
                                                    {["Staff Name", "Start Time", "End Time", "Duration", "Distance", "Client", "Map"].map((h, i) => (
                                                        <th key={i} style={{
                                                            padding: "12px 14px", textAlign: i === 6 ? "center" : "left",
                                                            fontWeight: 600, fontSize: 11.5, whiteSpace: "nowrap",
                                                            letterSpacing: 0.5, textTransform: "uppercase",
                                                            borderRight: i < 6 ? "1px solid rgba(255,255,255,0.15)" : "none",
                                                        }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {todayStaff.map((s) => {
                                                    const staffData = todayStaffStats[s.staff_id];
                                                    const isLoading = todayStatsLoading && !staffData;
                                                    const sessionsForStaff = staffData?.sessions || [];

                                                    if (isLoading) {
                                                        return (
                                                            <tr key={s.staff_id} style={{ borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
                                                                <td style={{ padding: "12px 14px" }}>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#667eea,#764ba2)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{s.staff_name.charAt(0).toUpperCase()}</div>
                                                                        <span style={{ fontWeight: 600, color: "#1d4ed8" }}>{s.staff_name}</span>
                                                                    </div>
                                                                </td>
                                                                <td colSpan={6} style={{ padding: "12px 14px", color: "#9ca3af", fontSize: 12 }}>⏳ Loading sessions…</td>
                                                            </tr>
                                                        );
                                                    }

                                                    if (sessionsForStaff.length === 0) {
                                                        return (
                                                            <tr key={s.staff_id} style={{ borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
                                                                <td style={{ padding: "12px 14px" }}>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#667eea,#764ba2)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{s.staff_name.charAt(0).toUpperCase()}</div>
                                                                        <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{s.staff_name}</span>
                                                                    </div>
                                                                </td>
                                                                <td colSpan={6} style={{ padding: "12px 14px", color: "#d1d5db", fontSize: 12 }}>No sessions with GPS data today</td>
                                                            </tr>
                                                        );
                                                    }

                                                    return sessionsForStaff.map((sess, si) => {
                                                        const detail = staffData.sessionDetails[sess.id];
                                                        const startTime = detail?.start_time || sess.start_time;
                                                        const endTime = detail?.end_time || sess.end_time;
                                                        const duration = detail?.duration || "—";
                                                        const dist = detail?.distance ? `${detail.distance.km} km` : (sess.distance_km ? `${sess.distance_km} km` : "—");
                                                        const isActive = !endTime;
                                                        const isExpanded = todayExpandedSession === sess.id;

                                                        // appointments for this session date
                                                        const sessDate = localDateStr(startTime);
                                                        const sessAppts = (staffData.appointments || []).filter((a) => localDateStr(a.appoint_appointmentdate) === sessDate);

                                                        const rowBg = isExpanded ? "#eef4ff" : si % 2 === 0 ? "#fff" : "#f9fafc";

                                                        return (
                                                            <React.Fragment key={sess.id}>
                                                                <tr style={{ background: rowBg, borderBottom: isExpanded ? "none" : "1px solid #f0f0f0", transition: "background 0.15s" }}>
                                                                    {/* Staff name — only on first session row */}
                                                                    <td style={{ padding: "12px 14px", verticalAlign: "middle" }}>
                                                                        {si === 0 ? (
                                                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                                                <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#667eea,#764ba2)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{s.staff_name.charAt(0).toUpperCase()}</div>
                                                                                <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{s.staff_name}</span>
                                                                            </div>
                                                                        ) : (
                                                                            <span style={{ fontSize: 11, color: "#d1d5db", paddingLeft: 38 }}>↳</span>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ padding: "12px 14px", color: "#374151", fontSize: 12, verticalAlign: "middle" }}>{fmt(startTime)}</td>
                                                                    <td style={{ padding: "12px 14px", verticalAlign: "middle" }}>
                                                                        {endTime ? <span style={{ color: "#374151", fontSize: 12 }}>{fmt(endTime)}</span>
                                                                            : <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#dcfce7", color: "#16a34a", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />Live</span>}
                                                                    </td>
                                                                    <td style={{ padding: "12px 14px", fontWeight: 600, color: "#374151", fontSize: 12, verticalAlign: "middle" }}>{duration}</td>
                                                                    <td style={{ padding: "12px 14px", verticalAlign: "middle" }}>
                                                                        {dist !== "—"
                                                                            ? <span style={{ display: "inline-block", padding: "2px 9px", background: "#faf5ff", color: "#7c3aed", borderRadius: 5, fontSize: 12, fontWeight: 700 }}>{dist}</span>
                                                                            : <span style={{ color: "#d1d5db" }}>—</span>}
                                                                    </td>
                                                                    <td style={{ padding: "12px 14px", verticalAlign: "middle", overflow: "hidden" }}>
                                                                        {sessAppts.length === 0 ? <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span> : (
                                                                            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                                                {sessAppts.map((appt, ai) => (
                                                                                    <button key={ai} onClick={() => setSelectedAppointment(appt)} style={{
                                                                                        display: "block", width: "100%", textAlign: "left",
                                                                                        padding: "2px 7px", border: "none",
                                                                                        background: "#fff7ed", color: "#c2410c",
                                                                                        borderRadius: 4, fontSize: 11, fontWeight: 600,
                                                                                        cursor: "pointer", whiteSpace: "normal", wordBreak: "break-word",
                                                                                    }}>{appt.appoint_clientname}</button>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ padding: "12px 14px", textAlign: "center", verticalAlign: "middle" }}>
                                                                        <button
                                                                            onClick={() => setTodayExpandedSession(isExpanded ? null : sess.id)}
                                                                            style={{
                                                                                padding: "5px 12px", borderRadius: 6, border: "none",
                                                                                background: isExpanded ? "#fee2e2" : "#eff6ff",
                                                                                color: isExpanded ? "#dc2626" : "#1d4ed8",
                                                                                fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                                                                            }}>
                                                                            {isExpanded ? "▲ Hide" : "🗺 Map"}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                                {isExpanded && detail?.locations && (
                                                                    <tr style={{ borderBottom: "3px solid #1976d2" }}>
                                                                        <td colSpan={7} style={{ padding: "0 16px 20px", background: "#eef4ff" }}>
                                                                            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 0 12px", borderBottom: "1px solid #c5d8f6", marginBottom: 14 }}>
                                                                                <span style={{ fontWeight: 700, color: "#1976d2", fontSize: 13 }}>🗺️ Path Map</span>
                                                                                <span style={{ fontSize: 11, color: "#6b7280" }}>· {s.staff_name}</span>
                                                                            </div>
                                                                            <SessionMap detail={detail} />
                                                                            <div style={{ display: "flex", gap: 14, marginTop: 12, fontSize: 11, color: "#555", flexWrap: "wrap" }}>
                                                                                {[
                                                                                    { color: "#8e24aa", label: "Start Point" },
                                                                                    { color: "#2196f3", label: "End Point" },
                                                                                    { color: "#ff9800", label: "GPS Checkpoints" },
                                                                                    { color: "#1976d2", label: "Travel Path", isLine: true },
                                                                                ].map(({ color, label, isLine }) => (
                                                                                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                                                        {isLine
                                                                                            ? <div style={{ width: 18, height: 3, background: color, borderRadius: 2 }} />
                                                                                            : <div style={{ width: 9, height: 9, background: color, borderRadius: "50%", border: "2px solid white", boxShadow: "0 0 3px rgba(0,0,0,0.2)" }} />}
                                                                                        <span>{label}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    });
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #f0f4ff", fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
                                        Click a staff name to view today's summary · Use the Staff dropdown above for full session history
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {loading && (
                    <div style={{
                        textAlign: "center", padding: 80, background: "white",
                        borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                    }}>
                        <div style={{ fontSize: 36, marginBottom: 14 }}>⏳</div>
                        <div style={{ color: "#1976d2", fontSize: 15, fontWeight: 600 }}>Loading data…</div>
                        <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 6 }}>Fetching sessions for {selectedStaffName}</div>
                    </div>
                )}

                {/* ── Back button (shown when staff selected) ── */}
                {selectedStaffId && !loading && (
                    <div className="no-print" style={{ marginBottom: 18 }}>
                        <button
                            onClick={() => handleStaffChange({ target: { value: "" } })}
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 7,
                                padding: "8px 16px", borderRadius: 8,
                                border: "1.5px solid #d1d5db", background: "white",
                                color: "#374151", fontSize: 13, fontWeight: 600,
                                cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.borderColor = "#9ca3af"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "white"; e.currentTarget.style.borderColor = "#d1d5db"; }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </button>
                    </div>
                )}

                {selectedStaffId && !loading && sessions.length === 0 && (
                    <div style={{
                        textAlign: "center", padding: "60px 0",
                        background: "white", borderRadius: 16,
                        boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                    }}>
                        <div style={{ fontSize: 44 }}>📭</div>
                        <div style={{ marginTop: 14, fontSize: 15, fontWeight: 600, color: "#374151" }}>
                            No sessions with GPS data found
                        </div>
                        <div style={{ marginTop: 6, fontSize: 13, color: "#9ca3af" }}>
                            {(fromDate || toDate) ? `for ${dateRangeLabel()}` : "for this staff member"}
                        </div>
                    </div>
                )}

                {sessions.length > 0 && (
                    <div id="printable-report">

                        <div className="print-only-title" style={{ display: "none" }}>
                            Staff Session Report
                        </div>

                        {/* ── Report Header ── */}
                        <div style={{
                            background: "white", borderRadius: 16,
                            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                            padding: "24px 28px", marginBottom: 18,
                            overflow: "hidden", position: "relative",
                        }}>


                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginTop: 4 }}>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Session Report</div>
                                    <h2 style={{ margin: 0, color: "#1a237e", fontSize: 22, fontWeight: 700 }}>{selectedStaffName}</h2>
                                </div>
                            </div>

                            {/* Stat cards */}
                            <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
                                {[
                                    { label: "Completed", value: sessions.filter((s) => !sessionDetails[s.id]?.is_active).length, bg: "#f0fdf4", val: "#1d4ed8", border: "#bbf7d0" },
                                    // { label: "Active", value: sessions.filter((s) => sessionDetails[s.id]?.is_active).length, bg: "#f0fdf4", val: "#1d4ed8", border: "#bbf7d0" },
                                    { label: "Total Distance", value: loadingDetails ? "…" : totalDist(), bg: "#f0fdf4", val: "#1d4ed8", border: "#bbf7d0" },
                                    { label: "Total Visits", value: totalVisitCount !== null ? totalVisitCount : "…", bg: "#f0fdf4", val: "#1d4ed8", border: "#bbf7d0" },
                                ].map((stat) => (
                                    <div key={stat.label} className="stat-card" style={{
                                        flex: "1 1 130px", background: stat.bg,
                                        border: `1.5px solid ${stat.border}`,
                                        borderRadius: 12, padding: "14px 18px",
                                        display: "flex", alignItems: "center", gap: 12,
                                    }}>
                                        <div style={{ fontSize: 22 }}>{stat.icon}</div>
                                        <div>
                                            <div style={{ fontSize: 10, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{stat.label}</div>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: stat.val, marginTop: 2 }}>{stat.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── Sessions Table ── */}
                        <div style={{
                            background: "white", borderRadius: 16,
                            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                            overflow: "hidden", marginBottom: 30,
                        }}>
                            <div className="table-scroll-wrapper">
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, tableLayout: "fixed" }}>
                                    <colgroup>
                                        <col style={{ width: "4%" }} />
                                        <col style={{ width: "16%" }} />
                                        <col style={{ width: "16%" }} />
                                        <col style={{ width: "13%" }} />
                                        <col style={{ width: "10%" }} />
                                        <col style={{ width: "9%" }} />
                                        <col style={{ width: "20%" }} />
                                        <col className="no-print" style={{ width: "12%" }} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: "linear-gradient(135deg, #1a237e, #1976d2)", color: "white" }}>
                                            {[
                                                { label: "#", align: "center", cls: "col-center" },
                                                { label: "Start Time", align: "left", cls: "col-left" },
                                                { label: "End Time", align: "left", cls: "col-left" },
                                                { label: "Duration", align: "left", cls: "col-left" },
                                                { label: "Distance", align: "left", cls: "col-left" },
                                                { label: "GPS Pts", align: "left", cls: "col-left" },
                                                { label: "Client", align: "left", cls: "col-left" },
                                                { label: "", align: "center", cls: "col-center", isMapCol: true },
                                            ].map((h, i) => (
                                                <th key={i} className={`${h.cls}${h.isMapCol ? " no-print" : ""}`} style={{
                                                    padding: "14px 16px", textAlign: h.align,
                                                    fontWeight: 600, fontSize: 12, whiteSpace: "nowrap",
                                                    letterSpacing: 0.5, textTransform: "uppercase",
                                                }}>{h.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sessions.map((session, idx) => {
                                            const detail = sessionDetails[session.id];
                                            const isExpanded = expandedSession === session.id;
                                            const isFetching = loadingPath === session.id;
                                            const sessionAppts = getSessionAppointments(session);
                                            const cellBase = { padding: "13px 16px", verticalAlign: "middle" };

                                            return (
                                                <React.Fragment key={session.id}>
                                                    <tr style={{
                                                        background: isExpanded ? "#eef4ff" : idx % 2 === 0 ? "#fff" : "#f9fafc",
                                                        borderBottom: isExpanded ? "none" : "1px solid #f0f0f0",
                                                        transition: "background 0.15s",
                                                    }}>
                                                        <td className="col-center" style={{ ...cellBase, textAlign: "center", color: "#9ca3af", fontWeight: 700, fontSize: 12 }}>{idx + 1}</td>
                                                        <td className="col-left" style={{ ...cellBase, color: "#374151", fontSize: 13 }}>{fmt(detail?.start_time || session.start_time)}</td>
                                                        <td className="col-left" style={{ ...cellBase, color: "#374151", fontSize: 13 }}>{detail?.end_time ? fmt(detail.end_time) : <span style={{ color: "#d1d5db" }}>—</span>}</td>
                                                        <td className="col-left" style={{ ...cellBase }}>
                                                            {detail?.duration
                                                                ? <span style={{ fontWeight: 600, color: "#374151" }}>{detail.duration}</span>
                                                                : <span style={{ color: "#d1d5db" }}>—</span>}
                                                        </td>
                                                        <td className="col-left" style={{ ...cellBase }}>
                                                            {detail?.distance
                                                                ? <span style={{
                                                                    display: "inline-block", padding: "3px 10px",
                                                                    background: "#faf5ff", color: "#7c3aed",
                                                                    borderRadius: 6, fontSize: 12, fontWeight: 700,
                                                                }}>{fmtDist(detail.distance)}</span>
                                                                : <span style={{ color: "#d1d5db" }}>—</span>}
                                                        </td>
                                                        <td className="col-left" style={{ ...cellBase, color: "#6b7280", fontSize: 13 }}>{detail?.distance?.points ?? session.location_points ?? "—"}</td>
                                                        <td className="col-left" style={{ ...cellBase, overflow: "hidden" }}>
                                                            {sessionAppts.length === 0 ? (
                                                                <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
                                                            ) : (
                                                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                                    {sessionAppts.map((appt, ai) => (
                                                                        <button key={ai} onClick={() => setSelectedAppointment(appt)} style={{
                                                                            display: "block", width: "100%", textAlign: "left",
                                                                            padding: "3px 8px", border: "none",
                                                                            background: "#fff7ed", color: "#c2410c",
                                                                            borderRadius: 5, fontSize: 12, fontWeight: 600,
                                                                            cursor: "pointer", whiteSpace: "normal", wordBreak: "break-word",
                                                                        }} title="Click to view appointment details">
                                                                            {appt.appoint_clientname}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="no-print" style={{ ...cellBase, textAlign: "center" }}>
                                                            <button className="no-print"
                                                                onClick={() => handleTogglePath(session.id)}
                                                                disabled={isFetching}
                                                                style={{
                                                                    padding: "6px 14px", borderRadius: 7, border: "none",
                                                                    cursor: isFetching ? "wait" : "pointer",
                                                                    background: isExpanded ? "#fee2e2" : "#eff6ff",
                                                                    color: isExpanded ? "#dc2626" : "#1d4ed8",
                                                                    fontSize: 12, fontWeight: 600,
                                                                    whiteSpace: "nowrap", transition: "all 0.15s",
                                                                }}>
                                                                {isFetching ? "⏳" : isExpanded ? "▲ Hide" : "🗺 Map"}
                                                            </button>
                                                        </td>
                                                    </tr>

                                                    {isExpanded && (
                                                        <tr className="no-print" style={{ borderBottom: "3px solid #1976d2" }}>
                                                            <td colSpan={9} style={{ padding: "0 20px 24px", background: "#eef4ff" }}>
                                                                <div style={{
                                                                    display: "flex", alignItems: "center", gap: 10,
                                                                    padding: "16px 0 14px", borderBottom: "1px solid #c5d8f6", marginBottom: 16,
                                                                }}>
                                                                    <span style={{ fontWeight: 700, color: "#1976d2", fontSize: 14 }}>🗺️ Path Map</span>
                                                                    <span style={{ fontSize: 12, color: "#6b7280" }}>· Session #{session.id}</span>
                                                                    {/* Geofence radius control */}
                                                                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                                                                        <label style={{ fontSize: 12, color: "#4b5563", fontWeight: 600 }}>
                                                                            Geofence Radius:
                                                                        </label>
                                                                        <select
                                                                            value={geofenceRadius}
                                                                            onChange={(e) => {
                                                                                const r = Number(e.target.value);
                                                                                setGeofenceRadius(r);
                                                                                // Re-fetch visit check with new radius
                                                                                axios.get(baseurladdress + `livetrack/session-visit-check/${session.id}?radius=${r}`, {
                                                                                    headers: { Authorization: `Bearer ${token}` }
                                                                                }).then(res => {
                                                                                    if (res.data.status === "Success") {
                                                                                        setVisitChecks(prev => ({ ...prev, [session.id]: res.data.data }));
                                                                                    }
                                                                                }).catch(console.error);
                                                                            }}
                                                                            style={{
                                                                                padding: "4px 10px", borderRadius: 6,
                                                                                border: "1.5px solid #93c5fd", fontSize: 12,
                                                                                background: "white", color: "#1d4ed8", fontWeight: 600,
                                                                                cursor: "pointer",
                                                                            }}>
                                                                            {[50, 100, 150, 200, 300, 500].map(r => (
                                                                                <option key={r} value={r}>{r} m</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                <SessionMap detail={detail} visitData={visitChecks[session.id]} geofenceRadius={geofenceRadius} />

                                                                {/* ── Map legend ── */}
                                                                <div style={{ display: "flex", gap: 18, marginTop: 14, fontSize: 12, color: "#555", flexWrap: "wrap" }}>
                                                                    {[
                                                                        { color: "#8e24aa", label: "Start Point" },
                                                                        { color: "#2196f3", label: "End Point" },
                                                                        { color: "#ff9800", label: "GPS Checkpoints" },
                                                                        { color: "#1976d2", label: "Travel Path", isLine: true },
                                                                        { color: "#16a34a", label: "Visited Client", isPin: true },    // ← green pin
                                                                        { color: "#dc2626", label: "Unvisited Client", isPin: true },
                                                                    ].map(({ color, label, isLine }) => (
                                                                        <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                            {isLine
                                                                                ? <div style={{ width: 22, height: 3, background: color, borderRadius: 2 }} />
                                                                                : <div style={{ width: 10, height: 10, background: color, borderRadius: "50%", border: "2px solid white", boxShadow: "0 0 3px rgba(0,0,0,0.25)" }} />
                                                                            }
                                                                            <span>{label}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                                {/* ── Visit Summary Table ── */}
                                                                {visitChecks[session.id] && visitChecks[session.id].length > 0 && (
                                                                    <div style={{ marginTop: 20 }}>
                                                                        <div style={{ fontWeight: 700, color: "#1a237e", fontSize: 13, marginBottom: 10 }}>
                                                                            Client Visit Summary
                                                                        </div>
                                                                        <div style={{ overflowX: "auto" }}>
                                                                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                                                                <thead>
                                                                                    <tr style={{ background: "linear-gradient(135deg,#1a237e,#1976d2)", color: "white" }}>
                                                                                        {["#", "Client", "Type", "Appt Time", "Status", "Dwell (min)"].map((h, i) => (
                                                                                            <th key={i} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, fontSize: 11, whiteSpace: "nowrap", letterSpacing: 0.4 }}>{h}</th>
                                                                                        ))}
                                                                                    </tr>
                                                                                </thead>
                                                                                  <tbody>
                                                                                    {visitChecks[session.id].map((v, vi) => {
                                                                                        const typeLabel = CLIENT_TYPE_LABEL[v.appoint_clienttype] || "—";
                                                                                        const fmtT = (t) => {
                                                                                            if (!t) return "—";
                                                                                            if (/^\d{2}:\d{2}/.test(String(t))) {
                                                                                                const [h, m] = t.split(":");
                                                                                                const d = new Date(); d.setHours(h, m, 0);
                                                                                                return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
                                                                                            }
                                                                                            return t;
                                                                                        };
                                                                                        return (
                                                                                            <tr key={vi} style={{ background: vi % 2 === 0 ? "#fff" : "#f0f6ff", borderBottom: "1px solid #e5e7eb" }}>
                                                                                                <td style={{ padding: "10px 12px", color: "#9ca3af", fontWeight: 700 }}>{vi + 1}</td>
                                                                                                <td style={{ padding: "10px 12px", fontWeight: 600, color: "#1e293b" }}>{v.appoint_clientname}</td>
                                                                                                <td style={{ padding: "10px 12px", color: "#6b7280" }}>{typeLabel}</td>
                                                                                                <td style={{ padding: "10px 12px", color: "#374151", fontSize: 12 }}>
                                                                                                    {fmtT(v.appoint_timefrom)} – {fmtT(v.appoint_timeto)}
                                                                                                </td>
                                                                                                <td style={{ padding: "10px 12px" }}>
                                                                                                    <span style={{
                                                                                                        display: "inline-block", padding: "3px 12px",
                                                                                                        borderRadius: 20, fontWeight: 700, fontSize: 12,
                                                                                                        background: v.visited ? "#dcfce7" : "#fee2e2",
                                                                                                        color: v.visited ? "#16a34a" : "#dc2626",
                                                                                                    }}>
                                                                                                        {v.visited ? "✅ Visited" : "❌ Not Visited"}
                                                                                                    </span>
                                                                                                </td>
                                                                                                <td style={{ padding: "10px 12px", color: "#374151" }}>
                                                                                                    {v.dwell_minutes > 0 ? `${v.dwell_minutes} min` : "—"}
                                                                                                </td>
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                        {/* Summary counts */}
                                                                   
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer */}
                            <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 12, padding: "14px 0 18px", borderTop: "1px solid #f0f0f0" }}>
                                Staff Path Tracking System &nbsp;·&nbsp; <strong style={{ color: "#6b7280" }}>{selectedStaffName}</strong> &nbsp;·&nbsp; {dateRangeLabel()}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Styles ── */}
            <style>{`
                .session-map { height: 420px; }
                @media (max-width: 640px) { .session-map { height: 260px; } }

                .table-scroll-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                .table-scroll-wrapper table { min-width: 700px; }

                @media (max-width: 600px) {
                    .controls-bar { padding: 10px 14px !important; gap: 8px !important; }
                    .controls-bar > div, .controls-bar > button { width: 100%; flex: 1 1 100% !important; }
                    .controls-bar > div[style*="width: 1px"] { display: none !important; }
                    .report-body { padding: 0 12px !important; margin-top: 16px !important; }
                }
                @media (max-width: 480px) {
                    .stat-card { flex: 1 1 calc(50% - 6px) !important; }
                }
                @media (max-width: 900px) {
                    .table-scroll-wrapper table { font-size: 12px !important; }
                    .table-scroll-wrapper th,
                    .table-scroll-wrapper td { padding: 10px 10px !important; }
                }

                @media print {
                    body { background: white !important; }
                    .no-print { display: none !important; }
                    #printable-report { padding: 0; margin: 0; }
                    @page { size: A4 landscape; margin: 0mm 12mm 8mm 12mm; }
                    table { page-break-inside: auto; }
                    tr { page-break-inside: avoid; }
                    thead { display: table-header-group; }
                    div, table { box-shadow: none !important; }
                    body > * { display: none !important; }
                    #printable-report, #printable-report * { display: revert !important; }
                    .no-print, #printable-report .no-print { display: none !important; }
                    .print-only-title {
                        display: block !important; text-align: center !important;
                        font-size: 20px !important; font-weight: 700 !important;
                        color: #1976d2 !important; padding: 14px 0 10px !important;
                        letter-spacing: 0.8px !important; border-bottom: 2px solid #1976d2 !important;
                        margin-bottom: 16px !important; width: 100% !important;
                    }
                    #printable-report th.col-left, 
                    #printable-report td.col-left {    text-align: left !important; }
                    #printable-report th.col-center,
                    #printable-report td.col-center { text-align: center !important; }
                    .table-scroll-wrapper { overflow: visible !important; }
                    .table-scroll-wrapper table { min-width: unset !important; }
                }

                input:focus, select:focus {
                    outline: none; border-color: #1976d2 !important;
                    box-shadow: 0 0 0 3px rgba(25,118,210,0.2);z  
                }   
                button:hover { opacity: 0.92; }
                tbody tr:hover td { background: #f0f6ff !important; }
            `}</style>
        </div>
    );
};


export default SessionTrackReport; 